import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { StyledTable } from 'common/Table/StyledTable';
import { CompanyAICompetitiveIntelligenceRow } from 'types/recommendations';

import CommunityCell from './AICICompanyCommunityCells/CommunityCell';
import DetailsCell from './AICICompanyCommunityCells/DetailsCell';
import PositionCell from './AICICompanyCommunityCells/PositionCell';
import ScoreCell from './AICICompanyCommunityCells/ScoreCell';
import TopCompetitorCell from './AICICompanyCommunityCells/TopCompetitorCell';
import AiCiTableWrapper from './AiCiTableWrapper';
import { AICI_COMMUNITY_FIELD } from './constants';
import { categoryLabels } from './helpers';

const SmallText = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const CategoryText = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const TableWrapper = styled(AiCiTableWrapper)`
  margin-top: 0;

  &&& .ant-table-tbody > tr > td.ant-table-cell {
    padding-left: 8px;
  }

  &&& .ant-table-thead > tr > th {
    font-size: 12px;
    font-family: var(--font-bold);
    padding-left: 8px;
    padding-top: 20px;
    padding-bottom: 10px;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }
`;

type Props = {
  rows: CompanyAICompetitiveIntelligenceRow[];
  category?: string;
  averageScore: number | null;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
};

const AICICompanyCommunityTable = ({ rows, category, averageScore, page, pageSize, total, onPageChange }: Props) => {
  const navigate = useNavigate();

  const openCommunityDetails = (communityId: number) => {
    navigate(`/ai-competitive-intelligence/overall-market-position?communityId=${communityId}`);
  };

  const scoreLabel = category ? 'Category Score' : 'Overall Score';

  const columns = [
    {
      title: 'PORTFOLIO RANK',
      dataIndex: AICI_COMMUNITY_FIELD.COMMUNITY_NAME,
      key: AICI_COMMUNITY_FIELD.COMMUNITY_NAME,
      render: (_: unknown, row: CompanyAICompetitiveIntelligenceRow, index: number) => (
        <CommunityCell
          position={(page - 1) * pageSize + index + 1}
          name={row.community_name}
          performance={row.performance}
        />
      ),
    },
    {
      title: category ? 'CATEGORY SCORE' : 'OVERALL SCORE',
      dataIndex: AICI_COMMUNITY_FIELD.SCORE,
      key: AICI_COMMUNITY_FIELD.SCORE,
      width: 170,
      render: (score: number | null) => <ScoreCell score={score} averageScore={averageScore} />,
    },
    ...(category
      ? [
          {
            title: 'CATEGORY',
            key: 'category',
            width: 320,
            render: () => <CategoryText>{category}</CategoryText>,
          },
        ]
      : [
          {
            title: 'STRONGEST',
            dataIndex: AICI_COMMUNITY_FIELD.STRONGEST_CATEGORIES,
            key: AICI_COMMUNITY_FIELD.STRONGEST_CATEGORIES,
            width: 160,
            render: (categories: string[]) =>
              categories.length ? <CategoryText>{categoryLabels(categories)}</CategoryText> : <SmallText>-</SmallText>,
          },
          {
            title: 'WEAKEST',
            dataIndex: AICI_COMMUNITY_FIELD.WEAKEST_CATEGORIES,
            key: AICI_COMMUNITY_FIELD.WEAKEST_CATEGORIES,
            width: 160,
            render: (categories: string[]) =>
              categories.length ? <CategoryText>{categoryLabels(categories)}</CategoryText> : <SmallText>-</SmallText>,
          },
        ]),
    {
      title: 'POSITION VS COMP.',
      dataIndex: AICI_COMMUNITY_FIELD.RANK,
      key: AICI_COMMUNITY_FIELD.RANK,
      width: 170,
      render: (rank: number | null, row: CompanyAICompetitiveIntelligenceRow) => (
        <PositionCell rank={rank} performance={row.performance} competitorsCount={row.competitors_count ?? 0} />
      ),
    },
    {
      title: 'TOP COMPETITOR',
      dataIndex: AICI_COMMUNITY_FIELD.TOP_COMPETITOR,
      key: AICI_COMMUNITY_FIELD.TOP_COMPETITOR,
      render: (competitor: CompanyAICompetitiveIntelligenceRow['top_competitor']) => (
        <TopCompetitorCell competitor={competitor} scoreLabel={scoreLabel} />
      ),
    },
    {
      title: '',
      key: 'details',
      width: 130,
      render: (_: unknown, row: CompanyAICompetitiveIntelligenceRow) => (
        <DetailsCell onClick={() => openCommunityDetails(row.community_id)} />
      ),
    },
  ];

  return (
    <TableWrapper>
      <StyledTable
        columns={columns as any}
        tableLayout="fixed"
        dataSource={rows}
        rowKey={((row: CompanyAICompetitiveIntelligenceRow) => row.community_id) as any}
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: page,
          pageSize,
          total,
          onChange: (pageNumber: number) => onPageChange(pageNumber),
          showTotal: (totalCount, range) => (
            <span>
              Showing {range[1] - range[0] + 1} of {totalCount} records
            </span>
          ),
        }}
        size="middle"
        bordered={false}
      />
    </TableWrapper>
  );
};

export default AICICompanyCommunityTable;
